// ── Board interaction: click to place node, drag to draw strands ──

import type { BoardState, PlayerColor, Vertex } from '../game/state';
import {
  validateNodePlacement,
  getValidStrandTargets,
  validateStrand,
  validatePath,
} from '../game/rules';
import type { Face } from '../game/topology';
import { BOARD_RADIUS, segmentsIntersect } from '../board/geometry';
import {
  highlightVertices,
  clearHighlights,
  showNodePreview,
  hideNodePreview,
  clearStrandPreviews,
  drawLivePolyline,
  drawCompletedStrands,
} from './renderer';

export interface StrandPath {
  targetId: string;
  waypoints: Array<{ x: number; y: number }>; // from node to target, player-drawn
}

export interface MoveInProgress {
  nodeX: number;
  nodeY: number;
  face: Face;
  strandPaths: StrandPath[];
  k: number;
}

export type InteractionCallback = (move: MoveInProgress) => void;

// ── State ──

type Phase = 'idle' | 'placeNode' | 'drawStrand';

interface InteractionState {
  phase: Phase;
  originalK: number;
  k: number;
  nodeX: number;
  nodeY: number;
  face: Face | null;
  completedStrands: StrandPath[];   // strands already drawn this turn
  dragging: boolean;                // currently dragging a strand path
  currentWaypoints: Array<{ x: number; y: number }>; // path being drawn
}

const MIN_WAYPOINT_DISTANCE = 0.12; // board-unit threshold between sampled drag points

let state: InteractionState = {
  phase: 'idle',
  originalK: 0,
  k: 0,
  nodeX: 0,
  nodeY: 0,
  face: null,
  completedStrands: [],
  dragging: false,
  currentWaypoints: [],
};

let currentSvg: SVGSVGElement | null = null;
let currentBoard: BoardState | null = null;
let currentColor: PlayerColor = 'black';
let onMoveComplete: InteractionCallback | null = null;
let onStateChange: (() => void) | null = null;

/** Convert SVG client coordinates to board coordinates */
function svgToBoard(svg: SVGSVGElement, clientX: number, clientY: number): { x: number; y: number } {
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  const svgPt = pt.matrixTransform(ctm.inverse());
  return { x: svgPt.x, y: -svgPt.y };
}

// ── Public API ──

/** Start the node placement phase */
export function beginNodePlacement(
  svg: SVGSVGElement,
  board: BoardState,
  k: number,
  color: PlayerColor,
  onComplete: InteractionCallback,
  onChange?: () => void,
): void {
  currentSvg = svg;
  currentBoard = board;
  currentColor = color;
  onMoveComplete = onComplete;
  onStateChange = onChange ?? null;

  state = {
    phase: 'placeNode',
    originalK: k,
    k,
    nodeX: 0,
    nodeY: 0,
    face: null,
    completedStrands: [],
    dragging: false,
    currentWaypoints: [],
  };

  clearHighlights(svg);
  clearStrandPreviews(svg);
  hideNodePreview(svg);
}

/** Handle pointer down on the board SVG */
export function handleBoardPointerDown(
  svg: SVGSVGElement,
  board: BoardState,
  clientX: number,
  clientY: number,
): void {
  const pos = svgToBoard(svg, clientX, clientY);
  currentBoard = board;

  if (state.phase === 'placeNode') {
    handleNodeClick(pos.x, pos.y, board);
  } else if (state.phase === 'drawStrand') {
    startDrag(pos.x, pos.y);
  }
}

/** Handle pointer move on the board SVG */
export function handleBoardPointerMove(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
): void {
  const pos = svgToBoard(svg, clientX, clientY);

  if (state.phase === 'placeNode') {
    showNodePreview(svg, pos.x, pos.y);
  } else if (state.phase === 'drawStrand' && state.dragging) {
    sampleDragPoint(pos.x, pos.y);
  }
}

/** Handle pointer up on the board SVG */
export function handleBoardPointerUp(
  svg: SVGSVGElement,
  board: BoardState,
  clientX: number,
  clientY: number,
): void {
  if (state.phase !== 'drawStrand' || !state.dragging) return;
  currentBoard = board;
  const pos = svgToBoard(svg, clientX, clientY);
  finishDrag(pos.x, pos.y, board);
}

/** Undo the last completed strand — remove it and stay in drawStrand */
export function undoLastStrand(): void {
  if (!currentSvg || !currentBoard) return;

  if (state.completedStrands.length > 0) {
    state.completedStrands.pop();
    state.dragging = false;
    state.currentWaypoints = [];
    clearStrandPreviews(currentSvg);

    // Redraw remaining completed strands
    drawCompletedStrands(currentSvg, state.completedStrands, currentColor);

    if (state.face) {
      const already = state.completedStrands.map(s => s.targetId);
      const validTargets = getValidStrandTargets(state.face, currentBoard, already);
      clearHighlights(currentSvg);
      highlightVertices(currentSvg, validTargets, currentBoard);
    }
  }

  onStateChange?.();
}

/** Undo node placement — go back to placeNode phase */
export function undoNodePlacement(): void {
  if (!currentSvg) return;

  state.phase = 'placeNode';
  state.k = state.originalK;
  state.nodeX = 0;
  state.nodeY = 0;
  state.face = null;
  state.completedStrands = [];
  state.dragging = false;
  state.currentWaypoints = [];

  clearHighlights(currentSvg);
  hideNodePreview(currentSvg);
  clearStrandPreviews(currentSvg);

  onStateChange?.();
}

/** Cancel the current interaction entirely */
export function cancelInteraction(): void {
  if (currentSvg) {
    clearHighlights(currentSvg);
    hideNodePreview(currentSvg);
    clearStrandPreviews(currentSvg);
  }
  state = {
    phase: 'idle', originalK: 0, k: 0,
    nodeX: 0, nodeY: 0, face: null,
    completedStrands: [], dragging: false, currentWaypoints: [],
  };
}

/** Get current interaction state for UI display */
export function getInteractionState(): {
  phase: string;
  strandsDrawn: number;
  strandsNeeded: number;
} {
  return {
    phase: state.phase,
    strandsDrawn: state.completedStrands.length,
    strandsNeeded: state.k,
  };
}

// ── Internal handlers ──

function handleNodeClick(x: number, y: number, board: BoardState): void {
  if (!currentSvg) return;

  const result = validateNodePlacement(x, y, board);
  if (!result.valid || !result.face) return;

  const validTargets = getValidStrandTargets(result.face, board, []);
  if (validTargets.length === 0) return;

  // Auto-reduce k if needed
  if (validTargets.length < state.k) {
    state.k = validTargets.length;
  }

  state.nodeX = x;
  state.nodeY = y;
  state.face = result.face;
  state.phase = 'drawStrand';
  state.completedStrands = [];

  showNodePreview(currentSvg, x, y);
  highlightVertices(currentSvg, validTargets, board);

  onStateChange?.();
}

/** Begin collecting waypoints for a drag gesture (must start near the node) */
function startDrag(x: number, y: number): void {
  const distToNode = Math.hypot(x - state.nodeX, y - state.nodeY);
  if (distToNode > 0.5) return; // must press near the node to start drawing

  state.dragging = true;
  // Path always starts at the exact node position
  state.currentWaypoints = [{ x: state.nodeX, y: state.nodeY }];
}

/** Sample a point during the drag, with minimum-distance filtering */
function sampleDragPoint(x: number, y: number): void {
  if (!currentSvg || state.currentWaypoints.length === 0) return;

  // Only inside the circle
  if (x * x + y * y >= BOARD_RADIUS * BOARD_RADIUS) return;

  const last = state.currentWaypoints[state.currentWaypoints.length - 1];
  const d = Math.hypot(x - last.x, y - last.y);
  if (d < MIN_WAYPOINT_DISTANCE) return;

  state.currentWaypoints.push({ x, y });

  // Live preview of the strand being drawn
  drawLivePolyline(currentSvg, state.currentWaypoints, currentColor, null);
}

/** Attempt to finish the drag at a valid target vertex */
function finishDrag(x: number, y: number, board: BoardState): void {
  if (!currentSvg || !state.face) {
    cancelDrag();
    return;
  }

  // Find nearest valid target
  const already = state.completedStrands.map(s => s.targetId);
  const validTargets = getValidStrandTargets(state.face, board, already);

  let nearest: string | null = null;
  let nearestDist = Infinity;
  for (const vid of validTargets) {
    const v = board.vertices[vid];
    if (!v) continue;
    const d = Math.hypot(v.x - x, v.y - y);
    if (d < nearestDist && d < 0.7) {
      nearestDist = d;
      nearest = vid;
    }
  }

  if (!nearest) {
    cancelDrag();
    return;
  }

  // Validate the target topologically
  const strandResult = validateStrand(
    { x: state.nodeX, y: state.nodeY },
    nearest, state.face, board, already,
  );
  if (!strandResult.valid) {
    cancelDrag();
    return;
  }

  const target = board.vertices[nearest]!;
  const fullPath = [...state.currentWaypoints, { x: target.x, y: target.y }];

  // Validate: no crossings with existing strands on the board
  const pathResult = validatePath(fullPath, board);
  if (!pathResult.valid) {
    rejectPath(fullPath);
    return;
  }

  // Check crossings against other strands drawn this turn
  for (const sp of state.completedStrands) {
    for (let i = 0; i < fullPath.length - 1; i++) {
      for (let j = 0; j < sp.waypoints.length - 1; j++) {
        if (segmentsIntersect(fullPath[i], fullPath[i + 1], sp.waypoints[j], sp.waypoints[j + 1])) {
          rejectPath(fullPath);
          return;
        }
      }
    }
  }

  // Accept the strand
  state.completedStrands.push({
    targetId: nearest,
    waypoints: fullPath,
  });

  state.dragging = false;
  state.currentWaypoints = [];

  // Clear live polyline
  const layer = currentSvg.querySelector('g.interaction');
  if (layer) layer.querySelectorAll('.live-polyline').forEach(p => p.remove());

  // Redraw all completed strands
  drawCompletedStrands(currentSvg, state.completedStrands, currentColor);

  // Check if we've drawn enough strands
  if (state.completedStrands.length >= state.k) {
    completeMove();
  } else {
    // Show targets for next strand
    const nowAlready = state.completedStrands.map(s => s.targetId);
    const nextTargets = state.face
      ? getValidStrandTargets(state.face, board, nowAlready)
      : [];

    if (nextTargets.length === 0 && state.completedStrands.length >= 1) {
      state.k = state.completedStrands.length;
      completeMove();
    } else {
      clearHighlights(currentSvg);
      highlightVertices(currentSvg, nextTargets, board);
      onStateChange?.();
    }
  }
}

/** Cancel a drag without accepting — clear the in-progress path */
function cancelDrag(): void {
  if (!currentSvg || !currentBoard) return;

  state.dragging = false;
  state.currentWaypoints = [];

  // Clear live polyline
  const layer = currentSvg.querySelector('g.interaction');
  if (layer) layer.querySelectorAll('.live-polyline').forEach(p => p.remove());

  // Redraw completed strands (in case they were overlaid)
  drawCompletedStrands(currentSvg, state.completedStrands, currentColor);
}

/** Flash the path red, then restore the completed strands */
function rejectPath(fullPath: Array<{ x: number; y: number }>): void {
  if (!currentSvg) return;
  drawLivePolyline(currentSvg, fullPath, '#ff0000' as any, null);
  state.dragging = false;
  state.currentWaypoints = [];
  setTimeout(() => {
    if (currentSvg) {
      drawCompletedStrands(currentSvg, state.completedStrands, currentColor);
      // Clear the red flash
      const layer = currentSvg.querySelector('g.interaction');
      if (layer) layer.querySelectorAll('.live-polyline').forEach(p => p.remove());
    }
  }, 400);
  onStateChange?.();
}

function completeMove(): void {
  if (!currentSvg || !state.face) return;

  const move: MoveInProgress = {
    nodeX: state.nodeX,
    nodeY: state.nodeY,
    face: state.face,
    strandPaths: [...state.completedStrands],
    k: state.k,
  };

  state.phase = 'idle';
  clearHighlights(currentSvg);
  hideNodePreview(currentSvg);
  clearStrandPreviews(currentSvg);

  onMoveComplete?.(move);
}
